import { useState } from 'react';
import { P } from '../palette';
import { sans } from '../typography';
import { ic } from '../icons';

export interface BtnProps {
  variant: 'primary' | 'secondary' | 'ghost' | 'destructive';
  label: string;
  icon?: string;
  disabled?: boolean;
  small?: boolean;
  loading?: boolean;
  flex?: boolean;
  /** 'submit' réservé au bouton principal d'un <form> — défaut 'button' pour
   *  qu'aucun Btn ne devienne un submit implicite dans un formulaire. */
  type?: 'button' | 'submit';
  onClick?: () => void;
}

const styles = {
  primary: { bg: P.accent, bgH: 'hsl(185,85%,46%)', text: P.s0, bdr: 'transparent' },
  secondary: { bg: 'transparent', bgH: 'hsla(210,15%,90%,0.06)', text: P.t1, bdr: P.border },
  ghost: { bg: 'transparent', bgH: 'hsla(210,15%,90%,0.05)', text: P.t2, bdr: 'transparent' },
  destructive: { bg: 'hsla(340,75%,58%,0.12)', bgH: 'hsla(340,75%,58%,0.20)', text: P.neg, bdr: 'hsla(340,75%,58%,0.25)' },
} as const;

export function Btn({ variant, label, icon, disabled, small, loading, flex, type = 'button', onClick }: BtnProps) {
  const [hovered, setHovered] = useState(false);
  const h = small ? 32 : 36;
  const px = small ? 10 : 16;
  const fs = small ? 12 : 13;
  const iSz = small ? 13 : 14;
  const s = styles[variant];

  return (
    <button
      type={type}
      onMouseEnter={() => { if (!disabled) setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: h,
        padding: `0 ${px}px`,
        fontFamily: sans,
        fontSize: fs,
        fontWeight: 600,
        lineHeight: 1,
        color: disabled ? P.disabled : s.text,
        background: disabled ? P.disabledBg : hovered ? s.bgH : s.bg,
        border: `1px solid ${disabled ? P.disabledBg : s.bdr}`,
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 100ms ease-out',
        outline: 'none',
        opacity: loading ? 0.7 : 1,
        whiteSpace: 'nowrap',
        flex: flex ? 1 : undefined,
      }}
    >
      {loading && (
        <svg
          width={iSz}
          height={iSz}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ animation: 'spin 800ms linear infinite' }}
        >
          <path d={ic.loader} />
        </svg>
      )}
      {!loading && icon && (
        <svg
          width={iSz}
          height={iSz}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={icon} />
        </svg>
      )}
      {label}
    </button>
  );
}
